'use client';

import classnames from 'classnames';
import React from 'react';

import NavigationDropdownMobile, { NavigationDropdownItem } from '@/components/navigation/NavigationDropdownMobile';

import { ShopLinkType } from '@cineamo/api/models/website-configuration/ShopLink.types';

import { getExternalLinkTarget } from '@/helpers/external-link-helper';

import { IconPopcornBag24 } from '@cineamo/ui/src/icons/ic-popcornBag';
import { IconProfile24 } from '@cineamo/ui/src/icons/ic-profile';
import { IconShoppingBag24 } from '@cineamo/ui/src/icons/ic-shoppingBag';
import { IconVoucher24 } from '@cineamo/ui/src/icons/ic-voucher';

import { useDomainConfiguration } from '@/providers/DomainConfigurationProvider';

type ShopDropdownItem = {
    href: string;
    title: string;
    type: ShopLinkType;
};

type ShopLinksDropdownMobileProps = {
    items: ShopDropdownItem[];
    triggerTitle: string;
};

function ShopLinkIcon({ type, className }: { type: ShopLinkType; className?: string }): React.JSX.Element {
    if (type === 'snack-shop') return <IconPopcornBag24 className={classnames('flex-none', className)} />;
    if (type === 'login') return <IconProfile24 className={classnames('flex-none', className)} />;
    return <IconVoucher24 className={classnames('flex-none', className)} />;
}

function ShopLinksDropdownMobile({ items, triggerTitle }: ShopLinksDropdownMobileProps): React.JSX.Element {
    const domainConfiguration = useDomainConfiguration();
    const externalLinkTarget = getExternalLinkTarget(domainConfiguration?.websiteConfiguration);

    const dropdownItems: NavigationDropdownItem[] = items.map((item) => {
        // Our own embedded shop pages are internal routes, so they must not inherit the
        // "open externally" target/rel configured for real shop URLs.
        const isEmbedded = item.href.startsWith('/embedded/');
        return {
            href: item.href,
            title: item.title,
            icon: <ShopLinkIcon type={item.type} />,
            item: item.type,
            target: isEmbedded ? undefined : externalLinkTarget.target,
            rel: isEmbedded ? undefined : externalLinkTarget.rel
        };
    });

    return (
        <NavigationDropdownMobile
            icon={<IconShoppingBag24 className="flex-none" />}
            triggerTitle={triggerTitle}
            items={dropdownItems}
        />
    );
}

export default ShopLinksDropdownMobile;
